import React from 'react';

import GraphQL from '~/types/graphql';

import BillingCycleConfiguratorSection from './components/BillingCycleConfiguratorSection';
import BusinessSection from './components/BusinessSection';
import CurrencyConfigurator from '../payments/CurrencyConfigurator';
import DesktopPlansComparison from './plans/DesktopPlansComparison';
import DesktopSignupScreenLayout from '~/components/patterns/screens/signup/layouts/DesktopSignupScreenLayout';
import EnterprisePlanOverview from './components/EnterprisePlanOverview';
import MobilePlansComparison from './plans/MobilePlansComparison';
import MobileSignupScreenLayout from '~/components/patterns/screens/signup/layouts/MobileSignupScreenLayout';
import PagesConfiguratorSection from './components/PagesConfiguratorSection';
import PlansOverviewSection from './components/PlansOverviewSection';
import StickyPlansComparison from './plans/StickyPlansComparison';

import useAccountCustomerType from '~/hooks/useAccountCustomerType';
import useAccountId from '~/hooks/useAccountId';
import useAccountIsOutOfBand from '~/hooks/useAccountIsOutOfBand';
import useAccountMaximumRegularSignupPageBundle from '~/hooks/useAccountMaximumRegularSignupPageBundle';
import useAccountSignup from '~/hooks/useAccountSignup';
import useAccountTariff from '~/hooks/useAccountTariff';
import useAllowedBillingCycles from '~/hooks/useAllowedBillingCycles';
import useAllowedPlans from '~/hooks/useAllowedPlans';
import useViewportWidth from '~/hooks/useViewportWidth';

import {
	LIST_OF_ALL_PLANS,
} from '~/model/universal';

import {
	verticalScrollTo,
} from '~/utilities/scrollTo';



const AccountSignup: React.FC = () => {
	const accountId = useAccountId();

	const accountCustomerType = useAccountCustomerType(accountId);
	const accountIsOutOfBand = useAccountIsOutOfBand(accountId);
	const accountMaximumRegularSignupPageBundle = useAccountMaximumRegularSignupPageBundle(accountId);
	const accountTariff = useAccountTariff(accountId);
	const allowedBillingCycles = useAllowedBillingCycles(accountId);
	const allowedPlans = useAllowedPlans(accountId);
	const accountSignup = useAccountSignup(accountId);
	const viewportWidth = useViewportWidth();

	const [focusedPlan, setFocusedPlan] = React.useState<GraphQL.AccountPlan | null>(null);

	const mobilePlansRef = React.useRef<HTMLDivElement | null>(null);
	const desktopPlansRef = React.useRef<HTMLDivElement | null>(null);

	React.useEffect(
		() => {
			const element = document.querySelector('.js-screen-scrollable-content');

			if (mobilePlansRef.current && element) {
				verticalScrollTo(element, mobilePlansRef.current.offsetTop + 30);
			}
		},
		[
			focusedPlan,
		],
	);

	if (
		accountId === null
		|| allowedBillingCycles === null
		|| allowedPlans === null
		|| accountTariff === null
	) {
		return null;
	}

	const effectiveFocusedPlan = focusedPlan ?? LIST_OF_ALL_PLANS[accountTariff][0];
	const showCurrencyConfigurator = accountIsOutOfBand !== true;
	const isAllowedOnlyEnterprisePlan = allowedPlans.every((plan) => (
		plan === GraphQL.AccountPlan.Enterprise
		|| plan === GraphQL.AccountPlan.EnterpriseLite
	));

	const renderMobileLayout = ({
		disabledPlans,
		mostPopularPlan,
		showChooseBillingCycle,
	}) => {
		if (isAllowedOnlyEnterprisePlan) {
			return (
				<MobileSignupScreenLayout
					currencyConfigurator={showCurrencyConfigurator && (
						<CurrencyConfigurator
							accountId={accountId}
						/>
					)}
					pagesConfigurator={(
						<>
							<PagesConfiguratorSection
								accountId={accountId}
								key={'pages-config-section-' + viewportWidth}
							/>
						</>
					)}
					selectedPlanOverview={(
						<EnterprisePlanOverview />
					)}
				/>
			);
		}

		return (
			<MobileSignupScreenLayout
				billingCycleConfigurator={showChooseBillingCycle && (
					<BillingCycleConfiguratorSection />
				)}
				currencyConfigurator={showCurrencyConfigurator && (
					<CurrencyConfigurator
						accountId={accountId}
					/>
				)}
				pagesConfigurator={(
					<PagesConfiguratorSection
						accountId={accountId}
						key={'pages-config-section-' + viewportWidth}
					/>
				)}
				plans={(
					<MobilePlansComparison
						disabledPlans={disabledPlans}
						focusedPlan={effectiveFocusedPlan}
						mostPopularPlan={mostPopularPlan}
						ref={mobilePlansRef}
					/>
				)}
				plansOverview={(
					<PlansOverviewSection
						accountId={accountId}
						disabledPlans={disabledPlans}
						onPlanClickCallback={setFocusedPlan}
					/>
				)}
			/>
		);
	};

	const renderDefaultLayout = ({
		bestChoicePlan,
		disabledPlans,
		mostPopularPlan,
		showChooseBillingCycle,
	}) => {
		if (isAllowedOnlyEnterprisePlan) {
			return (
				<DesktopSignupScreenLayout
					businessSection={(
						<BusinessSection
							benefits={false}
							customerType={accountCustomerType}
						/>
					)}
					currencyConfigurator={showCurrencyConfigurator && (
						<CurrencyConfigurator
							accountId={accountId}
						/>
					)}
					pagesConfigurator={(
						<PagesConfiguratorSection
							accountId={accountId}
							key={'pages-config-section-' + viewportWidth}
						/>
					)}
					selectedPlanOverview={(
						<EnterprisePlanOverview />
					)}
				/>
			);
		}

		return (
			<DesktopSignupScreenLayout
				billingCycleConfigurator={showChooseBillingCycle && (
					<BillingCycleConfiguratorSection />
				)}
				businessSection={(
					<BusinessSection
						customerType={accountCustomerType}
					/>
				)}
				currencyConfigurator={showCurrencyConfigurator && (
					<CurrencyConfigurator
						accountId={accountId}
					/>
				)}
				pagesConfigurator={(
					<PagesConfiguratorSection
						accountId={accountId}
						key={'pages-config-section-' + viewportWidth}
					/>
				)}
				plans={(
					<>
						<StickyPlansComparison
							disabledPlans={disabledPlans}
							plansRef={desktopPlansRef}
						/>
						<DesktopPlansComparison
							bestChoicePlan={bestChoicePlan}
							disabledPlans={disabledPlans}
							mostPopularPlan={mostPopularPlan}
							ref={desktopPlansRef}
						/>
					</>
				)}
			/>
		);
	};

	const showChooseBillingCycle = allowedBillingCycles.length > 1;

	const disabledPlans: Record<string, 'accountTooLarge' | 'disallowed'> = {};

	LIST_OF_ALL_PLANS[accountTariff].forEach((plan) => {
		if (
			plan !== GraphQL.AccountPlan.Enterprise
			&& !allowedPlans.includes(plan)
		) {
			disabledPlans[plan as string] = 'disallowed';
		} else if (
			plan !== GraphQL.AccountPlan.Enterprise
			&& accountSignup.pageBundle !== null
			&& accountMaximumRegularSignupPageBundle !== null
			&& accountSignup.pageBundle > accountMaximumRegularSignupPageBundle
		) {
			disabledPlans[plan as string] = 'accountTooLarge';
		}
	});

	let bestChoicePlan;
	let mostPopularPlan;

	if (!allowedPlans.includes(GraphQL.AccountPlan.Enterprise)) {
		bestChoicePlan = GraphQL.AccountPlan.Pro;
		mostPopularPlan = GraphQL.AccountPlan.Standard;
	}

	if (disabledPlans[bestChoicePlan] !== undefined) {
		bestChoicePlan = null;
	}

	if (disabledPlans[mostPopularPlan] !== undefined) {
		mostPopularPlan = null;
	}

	if (viewportWidth <= 800) {
		return renderMobileLayout({
			disabledPlans,
			mostPopularPlan,
			showChooseBillingCycle,
		});
	}

	return renderDefaultLayout({
		bestChoicePlan,
		disabledPlans,
		mostPopularPlan,
		showChooseBillingCycle,
	});
};



export default AccountSignup;
